import { useState, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { NavigationMenu } from '../components/NavigationMenu';
import { 
  Check,
  Plus,
  Minus,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { PageHeader } from '../components/PageHeader';
import { subscriptionApi, CreateSubscriptionRequest, TariffPlan } from '../services/subscriptionApi';

// Тип для скидок подписки
interface SubscriptionDiscount {
  id: string;
  months: number;
  discountPercent: number;
  isActive: boolean;
}
import './LandingPage.css';



// Функция для форматирования лимитов
const formatLimit = (limit: number): string => {
  if (limit === -1 || limit === 0) return 'без ограничений';
  return limit.toString();
};

// Вспомогательная функция для склонения слов
const getPlural = (count: number, one: string, two: string, five: string) => {
  if (count === -1 || count === 0) return five; // Для безлимитных
  const n = Math.abs(count);
  if (n % 100 >= 5 && n % 100 <= 20) {
    return five;
  }
  if (n % 10 === 1) {
    return one;
  }
  if (n % 10 >= 2 && n % 10 <= 4) {
    return two;
  }
  return five;
};

// Получить тариф по типу и имени
const getTariffByTypeAndName = (tariffs: TariffPlan[], type: string, name: string) => {
  return tariffs.find(t => t.type === type && t.name === name) || null;
};

// Получить тариф по типу (для Individual тарифов берем первый попавшийся)
const getTariffByType = (tariffs: TariffPlan[], type: string) => {
  return tariffs.find(t => t.type === type) || null;
};

// Получить базовую цену тарифа в рублях
const getTariffBasePrice = (tariffs: TariffPlan[], tariffType: string, tariffName?: string) => {
  let tariff;
  if (tariffName) {
    tariff = getTariffByTypeAndName(tariffs, tariffType, tariffName);
  } else {
    tariff = getTariffByType(tariffs, tariffType);
  }
  return tariff ? Math.round(tariff.basePrice) : 0; // Предполагаем что цена в долларах, конвертируем в рубли
};

// Получить цену за месяц с учетом скидки
const getMonthlyPrice = (tariffs: TariffPlan[], subscriptionDiscounts: SubscriptionDiscount[], selectedPeriod: number, tariffType: string, tariffName?: string) => {
  const basePrice = getTariffBasePrice(tariffs, tariffType, tariffName);
  // Применяем скидку к месячной цене
  const periodDiscount = getDiscountForPeriod(subscriptionDiscounts || [], selectedPeriod);
  if (periodDiscount && periodDiscount.discountPercent > 0) {
    return Math.round(basePrice * (1 - periodDiscount.discountPercent / 100));
  }
  return basePrice;
};

// Рассчитать итоговую цену за период
const calculatePriceWithDiscount = (tariffs: TariffPlan[], subscriptionDiscounts: SubscriptionDiscount[], selectedPeriod: number, tariffType: string, tariffName?: string) => {
  const monthlyPrice = getMonthlyPrice(tariffs, subscriptionDiscounts || [], selectedPeriod, tariffType, tariffName);
  return monthlyPrice * selectedPeriod;
};

// Получить скидку по выбранному периоду
const getDiscountForPeriod = (subscriptionDiscounts: SubscriptionDiscount[], months: number): SubscriptionDiscount | null => {
  if (!Array.isArray(subscriptionDiscounts)) {
    return null;
  }
  return subscriptionDiscounts.find(d => d.months === months && d.isActive) || null;
};

function Tariffs() {
  useDocumentTitle('Тарифы');
  const { profile, subscription, hasActiveSubscription, token, checkSubscription } = useAuth();
  const navigate = useCustomNavigate();
  const queryClient = useQueryClient();
  const [teamSize, setTeamSize] = useState(5);
  
  // Состояния для обработки платежей
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // Локальный флаг мгновенной отмены для UI
  const [justCancelled, setJustCancelled] = useState(false);
  
  // Состояния для смены тарифа
  const [showChangePlanConfirm, setShowChangePlanConfirm] = useState(false);
  const [newPlanId, setNewPlanId] = useState<string | null>(null);
  const [isNewPlanTeam, setIsNewPlanTeam] = useState(false);
  const [planChangeStatus, setPlanChangeStatus] = useState<'idle' | 'cancelling' | 'creating'>('idle');
  
  // Состояния для тарифов
  const [tariffs, setTariffs] = useState<TariffPlan[]>([]);
  const [tariffsLoading, setTariffsLoading] = useState(true);
  const [tariffsError, setTariffsError] = useState<string | null>(null);
  // Скидка до первой оплаты
  const [discountPercent, setDiscountPercent] = useState<number>(0);

  // Состояния для периодов оплаты
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1); // 1, 6, или 12 месяцев
  const [subscriptionDiscounts, setSubscriptionDiscounts] = useState<SubscriptionDiscount[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<number[]>([1]); // Доступные периоды из API

  // Хелпер для перезагрузки тарифов
  const refreshTariffs = async () => {
    try {
      setTariffsLoading(true);
      setTariffsError(null);
      const loadedTariffs = await subscriptionApi.getTariffs();
      setTariffs(loadedTariffs);
    } catch (error: any) {
      console.error('Ошибка загрузки тарифов:', error);
      setTariffsError(error.message || 'Ошибка загрузки тарифов');
    } finally {
      setTariffsLoading(false);
    }
  };

  // Мутация для отмены подписки
  const cancelSubscriptionMutation = useMutation({
    mutationFn: subscriptionApi.cancelSubscription,
    onSuccess: async (data: { success: boolean; message?: string }) => {
      // Инвалидируем запрос профиля пользователя, чтобы получить свежие данные о подписке
      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      await queryClient.refetchQueries({ queryKey: ['userProfile'] });
      // Обновляем подписку в AuthContext
      if (typeof checkSubscription === 'function') {
        await checkSubscription();
      }
      // Обновляем тарифы после успешной отмены
      if (data?.success) {
        // Оптимистично скрываем текущий тариф до прихода новых данных
        setJustCancelled(true);
        refreshTariffs();
      }
    },
    onError: (error: any) => {
      console.error('Ошибка отмены подписки:', error);
      setPaymentError(error.message || 'Ошибка отмены подписки');
      // Если сессия истекла, перенаправляем на логин
      if (error.message?.includes('Сессия истекла') || error.message?.includes('авторизация')) {
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      }
    },
    onSettled: () => {
      setShowCancelConfirm(false);
    }
  });


  // Загрузка тарифов при монтировании компонента
  useEffect(() => {
    refreshTariffs();
  }, []);

  // Скрываем карточку в тарифах, содержащую текст про интернет-магазин
  useEffect(() => {
    const pricingCards = document.querySelectorAll('.pricing-card');
    pricingCards.forEach((card) => {
      const description = card.querySelector('.plan-description');
      const title = card.querySelector('h3');
      if ((description && (description.textContent?.includes('Интернет-магазин') || description.textContent?.includes('магазин'))) ||
          (title && (title.textContent?.includes('Интернет-магазин') || title.textContent?.includes('магазин')))) {
        (card as HTMLElement).classList.add('hide-internet-shop');
      }
    });
  }, [tariffs]);

  // Загружаем доступную скидку до первой оплаты (для авторизованных)
  useEffect(() => {
    const loadPendingDiscount = async () => {
      try {
        if (!profile || !token) return;
        const resp = await fetch(`${API_URL}/subscriptionpayment/pending-discount`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (typeof data?.percent === 'number' && data.percent > 0) {
          setDiscountPercent(data.percent);
        } else {
          setDiscountPercent(0);
        }
      } catch (e) {
        // тихо игнорируем, скидка не обязательна
      }
    };

    loadPendingDiscount();
  }, [profile, token]);

  // Загружаем скидки по периодам
  useEffect(() => {
    const loadSubscriptionDiscounts = async () => {
      try {
        const response = await fetch(`${API_URL}/subscriptiondiscount/all`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const discounts = await response.json();
          setSubscriptionDiscounts(Array.isArray(discounts) ? discounts : []);

          // Извлекаем доступные периоды из скидок (всегда включаем 1 месяц)
          const periods = [1]; // Всегда показываем 1 месяц
          discounts.forEach((discount: SubscriptionDiscount) => {
            if (discount.months > 1 && !periods.includes(discount.months)) {
              periods.push(discount.months);
            }
          });
          setAvailablePeriods(periods.sort((a, b) => a - b));

          // Устанавливаем первый доступный период как выбранный по умолчанию
          if (periods.length > 0 && !periods.includes(selectedPeriod)) {
            setSelectedPeriod(periods[0]);
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки скидок:', error);
        // Продолжаем без скидок
      }
    };

    loadSubscriptionDiscounts();
  }, []);

  // Фильтруем тарифы - исключаем пробные/триальные и бесплатные тарифы
  const filteredTariffs = tariffs.filter(t => 
    !t.isTrial &&
    t.basePrice > 0
  ).sort((a, b) => a.basePrice - b.basePrice); // Сортируем по цене для стабильности
  
  // Проверка текущего тарифа пользователя
  const isSubscriptionUserCancelled = (subscription?.status === 'UserCancelled') || justCancelled;
  const currentTariffId = isSubscriptionUserCancelled ? null : subscription?.tariffPlanId;
  const currentTariff = filteredTariffs.find(t => t.id === currentTariffId);

  const createSubscription = async (planId: string, isTeam: boolean) => {
    setProcessingPlanId(planId);
    setPaymentError(null);

    try {
      const request: CreateSubscriptionRequest = {
        TariffPlanId: planId,
        Months: selectedPeriod,
        TeamName: isTeam
          ? `Команда ${profile?.nickname || 'Пользователь'}`
          : null
      };

      // Для командного тарифа добавляем размер команды
      if (isTeam) {
        request.TeamSize = teamSize;
      }

      const response = await subscriptionApi.createSubscription(request);

      if (response.success && response.redirectUrl) {
        // Перенаправляем пользователя на страницу оплаты Точка Банк
        window.location.href = response.redirectUrl;
      } else {
        setPaymentError(response.error || 'Ошибка создания подписки');
      }
    } catch (error: any) {
      console.error('Ошибка создания подписки:', error);
      setPaymentError(error.message || 'Ошибка создания подписки');
      
      // Если сессия истекла, перенаправляем на логин
      if (error.message?.includes('Сессия истекла') || error.message?.includes('авторизация')) {
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      }
    } finally {
      setProcessingPlanId(null);
    }
  };

  const handleCancelSubscription = async () => {
    cancelSubscriptionMutation.mutate();
  };

  const handleChangePlan = async () => {
    if (!newPlanId) return;
    
    setPaymentError(null);

    try {
      await createSubscription(newPlanId, isNewPlanTeam);
      
    } catch (error: any) {
      console.error('Ошибка смены тарифа:', error);
      setPaymentError(error.message || 'Ошибка смены тарифа');
      
      // Если сессия истекла, перенаправляем на логин
      if (error.message?.includes('авторизация')) {
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      }
    } finally {
      // Сбрасываем состояния в любом случае
      setShowChangePlanConfirm(false);
      setNewPlanId(null);
      setIsNewPlanTeam(false);
    }
  };

  // Расчет цены командного тарифа на основе данных из базы (используется в старой логике)
  const calculateTeamPrice = (members: number, tariff: TariffPlan | null) => {
    if (!tariff) return 0;

    // В API цена за 5 участников уже включена в basePrice
    const baseMembers = 5;
    const additionalMembers = Math.max(0, members - baseMembers);
    return tariff.basePrice + (tariff.pricePerMember * additionalMembers);
  };

  // Рассчитать итоговую цену с учетом периода и скидок
  const calculateFinalPrice = (basePrice: number, isTeam: boolean, teamSize: number = 5): number => {
    let finalPrice = basePrice;

    // Для командных тарифов рассчитываем цену с учетом количества участников
    if (isTeam) {
      const baseMembers = 5;
      const additionalMembers = Math.max(0, teamSize - baseMembers);
      finalPrice = basePrice + (additionalMembers * (basePrice * 0.6)); // Предполагаем, что цена за доп. участника 60% от базовой
    }

    // Применяем скидку за период оплаты
    const periodDiscount = getDiscountForPeriod(subscriptionDiscounts, selectedPeriod);
    if (periodDiscount && periodDiscount.discountPercent > 0) {
      finalPrice = applyDiscount(finalPrice, periodDiscount.discountPercent);
    }

    // Применяем скидку до первой оплаты (если есть)
    if (discountPercent > 0 && !hasActiveSubscription) {
      finalPrice = applyDiscount(finalPrice, discountPercent);
    }

    return finalPrice;
  };

  // Вспомогательная функция для применения скидки
  const applyDiscount = (price: number, percent: number): number => {
    if (!percent || percent <= 0) return price;
    const discounted = price * (1 - percent / 100);
    return Math.round(discounted);
  };

  // Безопасное форматирование даты окончания подписки
  const formatSubscriptionEndDate = () => {
    if (!subscription?.endDate) return '';
    try {
      return new Date(subscription.endDate).toLocaleDateString('ru-RU');
    } catch {
      return '';
    }
  };

  return (
    <div className="landing min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding">
      {profile && (
        <div className="hidden md:block">
          <NavigationMenu />
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Тарифные планы"
          subtitle="Выберите тарифный план, который лучше всего подходит для ваших потребностей"
        />

        {/* Состояние загрузки тарифов */}
        {tariffsLoading && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600 dark:text-gray-400">Загружаем тарифы...</p>
          </div>
        )}

        {/* Ошибка загрузки тарифов */}
        {tariffsError && (
          <div className="max-w-md mx-auto mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">
              {tariffsError}
            </p>
          </div>
        )}
        
        {/* Pricing Section - динамическое отображение тарифов */}
        {!tariffsLoading && !tariffsError && (
        <>
          {/* Переключатели периодов оплаты - показываем только если есть больше одного периода */}
          {availablePeriods.length > 1 && (
            <div className="flex justify-center mb-8 overflow-x-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-1 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-nowrap min-w-max">
              {availablePeriods.map((period) => {
                const periodDiscount = getDiscountForPeriod(subscriptionDiscounts || [], period);
                const hasDiscount = periodDiscount && periodDiscount.discountPercent > 0;

                return (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      selectedPeriod === period
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                        : 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {period} {getPlural(period, 'месяц', 'месяца', 'месяцев')}
                    {hasDiscount && (
                      <span className={`ml-1.5 md:ml-2 px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full text-xs font-medium ${
                        selectedPeriod === period
                          ? 'bg-white/20 text-white'
                          : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
                      }`}>
                        -{periodDiscount.discountPercent}%
                      </span>
                    )}
                  </button>
                );
              })}
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <div className="pricing-grid" style={{gridTemplateColumns: `repeat(${Math.min(filteredTariffs.length, 3)}, 1fr)`, maxWidth: '1100px'}}>
            {filteredTariffs.map((tariff) => {
              const isCurrent = currentTariff?.id === tariff.id && hasActiveSubscription && !isSubscriptionUserCancelled;
              const isTeam = tariff.type === 'Team';
              const isCancelledCurrentForThisTariff =
                !isCurrent &&
                subscription?.status === 'UserCancelled' &&
                subscription?.tariffPlanId === tariff.id &&
                (() => {
                  try {
                    return subscription?.endDate ? new Date(subscription.endDate) > new Date() : false;
                  } catch {
                    return false;
                  }
                })();
              
              // Логика для определения популярного тарифа. 
              const individualPaidTariffs = filteredTariffs.filter(t => t.type === 'Individual');
              const isPopular = individualPaidTariffs.length > 1 && individualPaidTariffs[1]?.id === tariff.id;

              // Динамический расчет цены для командного тарифа с учетом периода и скидок
              const basePrice = isTeam ? calculateTeamPrice(teamSize, tariff) : tariff.basePrice;
              const finalPrice = calculateFinalPrice(basePrice, isTeam, teamSize);
              const monthlyPrice = getMonthlyPrice(tariffs, subscriptionDiscounts, selectedPeriod, tariff.type, tariff.name);

              return (
                <div key={tariff.id} className={`pricing-card ${isPopular ? 'popular' : ''} ${isCurrent ? 'current-plan' : ''}`}>
                  {isCurrent ? (
                  <div className="current-plan-badge">Текущий план</div>
                  ) : isPopular ? (
                  <div className="popular-badge">Популярный</div>
                  ) : null}

                  <h3>{tariff.name}</h3>
                  <p className="plan-description">{tariff.description}</p>

                  <div className="price" style={{ marginBottom: '1rem' }}>
                    {finalPrice !== basePrice ? (
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1}}>
                      <span>
                          {monthlyPrice.toLocaleString('ru-RU')} ₽
                        <span className="price-note">в месяц</span>
                      </span>
                      <div className={`pricing-total-price ${(availablePeriods.length <= 1 || selectedPeriod === 1) ? 'empty' : ''}`}>
                        {(availablePeriods.length <= 1 || selectedPeriod === 1) ? '' : `Итого: ${(finalPrice * selectedPeriod).toLocaleString('ru-RU')} ₽`}
                      </div>
                    </div>
                  ) : (
                    <>
                        {monthlyPrice.toLocaleString('ru-RU')} ₽
                      <span className="price-note">в месяц</span>
                      <div className={`pricing-total-price ${(availablePeriods.length <= 1 || selectedPeriod === 1) ? 'empty' : ''}`}>
                        {(availablePeriods.length <= 1 || selectedPeriod === 1) ? '' : `Итого: ${(finalPrice * selectedPeriod).toLocaleString('ru-RU')} ₽`}
                      </div>
                    </>
                  )}
                </div>

                  {discountPercent > 0 && !isCurrent && (
                  <div style={{textAlign: 'center'}}>
                    <div className="text-xs text-emerald-600 mt-1">{`Скидка ${discountPercent}%`}</div>
                    <div className="text-[10px] text-gray-500">по коду приглашения</div>
                  </div>
                )}

                  {/* Интерактивный калькулятор для командного тарифа */}
                  {isTeam && (
                <div style={{margin: '16px 0', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                  <div style={{textAlign: 'center', marginBottom: '8px'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '8px'}}>
                      Количество участников
                    </label>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'}}>
                      <button
                            onClick={() => setTeamSize(Math.max(5, teamSize - 1))}
                            disabled={teamSize <= 5}
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                              cursor: teamSize <= 5 ? 'not-allowed' : 'pointer',
                              opacity: teamSize <= 5 ? 0.5 : 1
                        }}
                      >
                        <Minus size={16} />
                      </button>
                      <div style={{minWidth: '32px', textAlign: 'center'}}>
                        <span style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827'}}>
                          {teamSize}
                        </span>
                      </div>
                      <button
                        onClick={() => setTeamSize(Math.min(20, teamSize + 1))}
                        disabled={teamSize >= 20}
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: teamSize >= 20 ? 'not-allowed' : 'pointer',
                          opacity: teamSize >= 20 ? 0.5 : 1
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                      {teamSize > 5 && (
                    <div style={{textAlign: 'center', fontSize: '0.75rem', color: '#6b7280'}}>
                          +{teamSize - 5} доп. участник{getPlural(teamSize - 5, '', 'а', 'ов')} × {Math.round(tariff.pricePerMember * 0.6).toLocaleString('ru-RU')} ₽ в месяц
                        </div>
                      )}
                      <div style={{textAlign: 'center', fontSize: '0.75rem', color: '#6b7280', marginTop: '4px'}}>
                        Итого: {(calculateFinalPrice(basePrice, true, teamSize) * selectedPeriod).toLocaleString('ru-RU')} ₽ за период
                      </div>
                    </div>
                  )}

                  {/* Кнопки действий */}
                  {isCurrent ? (
                  subscription?.status === 'UserCancelled' ? (
                    <div className="text-center py-2 text-gray-600 dark:text-gray-400">
                      Ваша подписка действует до {formatSubscriptionEndDate()}
                    </div>
                  ) : (
                    <button
                      className="btn-secondary"
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={cancelSubscriptionMutation.isPending}
                    >
                      {cancelSubscriptionMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Отмена...
                        </>
                      ) : (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Отменить подписку
                        </>
                      )}
                    </button>
                  )
                ) : (
                  <>
                    {isCancelledCurrentForThisTariff && (
                      <div className="text-center py-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800">
                          Тариф действует до {formatSubscriptionEndDate()}
                        </span>
                      </div>
                    )}
                  <button
                    className="btn-primary py-2"
                      onClick={() => {
                        // Для пробного тарифа не показываем диалог смены, сразу создаем подписку
                        if (hasActiveSubscription && currentTariff && !currentTariff.isTrial && !isSubscriptionUserCancelled) {
                            setNewPlanId(tariff.id);
                            setIsNewPlanTeam(isTeam);
                            setShowChangePlanConfirm(true);
                        } else {
                            createSubscription(tariff.id, isTeam);
                        }
                      }}
                      disabled={processingPlanId === tariff.id}
                    >
                      {processingPlanId === tariff.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Обработка...
                      </>
                    ) : (
                      profile ? 'Подключить' : 'Выбрать'
                    )}
                  </button>
                  </>
                )}

                <div className="features-section">
                  <h4>Это включает:</h4>
                </div>
                <ul className="features-list">
                    {isTeam ? (
                      <>
                        <li><Check size={16} /> Всё из тарифа Профи</li>
                        <li><Check size={16} /> {teamSize} участник{getPlural(teamSize, '', 'а', 'ов')}</li>
                        <li><Check size={16} /> Доп. участник +{tariff.pricePerMember.toLocaleString('ru-RU')} ₽</li>
                      </>
                    ) : (
                      <>
                        <li><Check size={16} /> {formatLimit(tariff.audienceAnalysisLimit)} анализ{getPlural(tariff.audienceAnalysisLimit, ' ЦА', 'а ЦА', 'ов ЦА')}</li>
                        <li><Check size={16} /> {formatLimit(tariff.contentPlanLimit)} контент-план{getPlural(tariff.contentPlanLimit, '', 'а', 'ов')}</li>
                        <li><Check size={16} /> {formatLimit(tariff.reelsAnalysisLimit)} анализ{getPlural(tariff.reelsAnalysisLimit, ' рилс', 'а рилс', 'ов рилс')}</li>
                      </>
                    )}
                </ul>
              </div>
              );
            })}
          </div>
        </div>
        </>
        )}

        {/* Отображение ошибок */}
        {paymentError && (
          <div className="max-w-md mx-auto mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">
              {paymentError}
            </p>
          </div>
        )}

        {/* Дополнительная информация */}
        <div className="text-center mt-8">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center">
              <Check className="w-3 h-3 mr-1 text-emerald-500" />
              Отмена в любое время
            </span>
            <span className="flex items-center">
              <Check className="w-3 h-3 mr-1 text-emerald-500" />
              Поддержка 24/7
            </span>
          </div>
        </div>
      </div>

      {/* Модальное окно подтверждения отмены подписки */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Отменить подписку?
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Вы уверены, что хотите отменить подписку? После отмены вы потеряете доступ к премиум функциям после окончания вашего текущего периода.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={cancelSubscriptionMutation.isPending}
              >
                Отмена
              </button>
              <button
                onClick={handleCancelSubscription}
                className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={cancelSubscriptionMutation.isLoading}
              >
                {cancelSubscriptionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Отмена...
                  </>
                ) : (
                  'Да, отменить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения смены тарифа */}
      {showChangePlanConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-blue-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Сменить тарифный план?
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              У вас уже есть активная подписка. Для подключения нового тарифа сначала будет отменена текущая подписка, а затем подключен новый план. Вы уверены, что хотите продолжить?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowChangePlanConfirm(false);
                  setNewPlanId(null);
                  setIsNewPlanTeam(false);
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={processingPlanId === newPlanId}
              >
                Отмена
              </button>
              <button
                onClick={handleChangePlan}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={processingPlanId === newPlanId}
              >
                {processingPlanId === newPlanId ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Переходим на новый тариф...
                  </>
                ) : (
                  'Да, сменить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Tariffs; 